import React, { useState } from 'react';
import {
    FaChevronDown,
    FaChevronUp,
    FaUserCheck,
    FaUserTimes,
    FaExclamationTriangle,
    FaLayerGroup,
} from 'react-icons/fa';
import FullProfileReview from './FullProfileReview';
import LicenceReview from './LicenceReview';
import TrainingReview from './TrainingReview';
import CompletionReview from './CompletionReview';

const STATUS_BADGE_STYLES = {
    pending: {
        bg: '#fffbeb',
        color: '#b45309',
        border: '#fde68a',
        label: 'Pending',
    },
    partially_approved: {
        bg: '#f5f3ff',
        color: '#6d28d9',
        border: '#ddd6fe',
        label: 'Partially Approved',
    },
    approved: {
        bg: '#ecfdf5',
        color: '#047857',
        border: '#a7f3d0',
        label: 'Approved',
    },
    rejected: {
        bg: '#fef2f2',
        color: '#b91c1c',
        border: '#fecaca',
        label: 'Rejected',
    },
};

/**
 * Extracts and formats candidate name from payload or fallback fields.
 */
const getCandidateName = (item) => {
    const pd = item.payload?.personal_details;
    if (pd) {
        const parts = [pd.first_name, pd.middle_name || pd.middle_names, pd.last_name].filter(Boolean);
        if (parts.length > 0) return parts.join(' ');
    }
    return item.candidateName || item.candidateUserID || item.payload?.user_id || item.payload?.userID || 'Unnamed Candidate';
};

/**
 * Extracts candidate email from payload or fallback fields.
 */
const getCandidateEmail = (item) => {
    return (
        item.payload?.personal_details?.email ||
        item.payload?.email ||
        item.candidateEmail ||
        'Email not supplied'
    );
};

/**
 * Extracts candidate external ID from payload or fallback fields.
 */
const getCandidateUserID = (item) => {
    return (
        item.candidateUserID ||
        item.payload?.user_id ||
        item.payload?.userID ||
        item.externalUserID ||
        '—'
    );
};

/**
 * Extracts candidate agencies list from payload or fallback fields.
 */
const getCandidateAgencies = (item) => {
    const pd = item.payload?.personal_details || {};
    const raw = pd.agencies ||
        pd.skills_and_agencies ||
        item.payload?.agencies ||
        item.payload?.skills_and_agencies ||
        item.agencies;
    if (Array.isArray(raw) && raw.length > 0) return raw.join(', ');
    if (typeof raw === 'string' && raw.trim()) return raw;
    return 'None';
};

/**
 * Extracts candidate source status.
 */
const getCandidateSourceStatus = (item) => {
    return item.payload?.status || item.sourceStatus || '—';
};

/**
 * Formats epoch timestamp or Firestore timestamp into human-readable string.
 */
const formatDate = (val) => {
    if (!val) return '—';
    try {
        if (typeof val === 'number') return new Date(val).toLocaleString();
        if (val._seconds) return new Date(val._seconds * 1000).toLocaleString();
        if (val.seconds) return new Date(val.seconds * 1000).toLocaleString();
        if (val.toDate && typeof val.toDate === 'function') return val.toDate().toLocaleString();
        const d = new Date(val);
        return isNaN(d.getTime()) ? '—' : d.toLocaleString();
    } catch {
        return '—';
    }
};

const PurgatoryCard = ({
    item,
    serverID,
    displayStatus = 'pending',
    isBusy = false,
    onApprove,
    onReject,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectError, setRejectError] = useState('');

    const candidateName = getCandidateName(item);
    const candidateEmail = getCandidateEmail(item);
    const employeeDocID = item.employeeDocumentID || item.matchedEmployeeID || item.employeeID || '';
    const badgeStyle = STATUS_BADGE_STYLES[displayStatus] || STATUS_BADGE_STYLES.pending;

    // Initials for avatar
    const initials = candidateName
        .split(' ')
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase() || 'CA';

    const handleConfirmReject = () => {
        const trimmed = rejectReason.trim();
        if (!trimmed) {
            setRejectError('Please provide a specific rejection reason.');
            return;
        }
        setShowRejectModal(false);
        setRejectReason('');
        setRejectError('');
        onReject(item, trimmed);
    };

    const isPending = (item.reviewStatus || 'pending') === 'pending';
    const isRejected = (item.reviewStatus || '') === 'rejected' || displayStatus === 'rejected';

    return (
        <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            border: `1px solid ${isExpanded ? '#cbd5e0' : '#e2e8f0'}`,
            marginBottom: '14px',
            boxShadow: isExpanded ? '0 4px 6px -1px rgba(0,0,0,0.07)' : '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'all 0.2s ease',
            overflow: 'hidden',
        }}>
            {/* Card Header (Always visible) */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 20px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    backgroundColor: isExpanded ? '#f8fafc' : '#ffffff',
                    borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
                    {/* Candidate Avatar */}
                    <div style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '50%',
                        backgroundColor: '#edf2f7',
                        color: '#2b6cb0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '14px',
                        flexShrink: 0,
                        border: '1px solid #cbd5e0',
                    }}>
                        {initials}
                    </div>

                    {/* Candidate Details */}
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexWrap: 'wrap',
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '15px',
                                fontWeight: '700',
                                color: '#2d3748',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                                {candidateName}
                            </h3>
                            {item.identityConflict && (
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    backgroundColor: '#fffaf0',
                                    color: '#c05621',
                                    border: '1px solid #fbd38d',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                }}>
                                    <FaExclamationTriangle /> Identity Conflict
                                </span>
                            )}
                        </div>
                        <div style={{
                            fontSize: '13px',
                            color: '#718096',
                            marginTop: '2px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                            {candidateEmail}
                            {item.employeeName && (
                                <span style={{ marginLeft: '8px', color: '#a0aec0' }}>
                                    • Matched: <strong style={{ color: '#4a5568' }}>{item.employeeName}</strong>
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Header: Status Badge & Chevron */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginLeft: '12px' }}>
                    <span style={{
                        backgroundColor: badgeStyle.bg,
                        color: badgeStyle.color,
                        border: `1px solid ${badgeStyle.border}`,
                        borderRadius: '16px',
                        padding: '4px 12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        whiteSpace: 'nowrap',
                    }}>
                        {badgeStyle.label}
                    </span>

                    <div style={{ color: '#a0aec0', fontSize: '14px' }}>
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                </div>
            </div>

            {/* Card Body (Visible when expanded) */}
            {isExpanded && (
                <div style={{ padding: '20px' }}>
                    {/* Identity Conflict Alert */}
                    {item.identityConflict && (
                        <div style={{
                            backgroundColor: '#fffaf0',
                            border: '1px solid #fbd38d',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            color: '#9c4221',
                            fontSize: '13px',
                        }}>
                            <FaExclamationTriangle style={{ fontSize: '18px', flexShrink: 0 }} />
                            <div>
                                <strong>Potential Identity Mismatch Detected:</strong> Candidate personal details differ significantly from the internal employee record. Proceed with caution.
                            </div>
                        </div>
                    )}

                    {/* Rejection Banner */}
                    {item.rejectionReason && (
                        <div style={{
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            marginBottom: '16px',
                            color: '#991b1b',
                            fontSize: '13px',
                        }}>
                            <strong>Rejection Reason:</strong> {item.rejectionReason}
                            {item.rejectedAt && (
                                <span style={{ display: 'block', fontSize: '12px', marginTop: '4px', color: '#b91c1c' }}>
                                    Rejected on: {formatDate(item.rejectedAt)}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Grid of Sections: Matched Employee & Source Metadata */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '16px',
                        marginBottom: '16px',
                    }}>
                        {/* Section 1: Matched Internal Employee */}
                        <div style={{
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            padding: '14px 16px',
                        }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: '#4a5568', textTransform: 'uppercase' }}>
                                Matched HA Employee
                            </h4>
                            <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div>
                                    <span style={{ color: '#718096' }}>Name: </span>
                                    <strong style={{ color: '#2d3748' }}>{item.employeeName || '—'}</strong>
                                </div>
                                <div>
                                    <span style={{ color: '#718096' }}>Email: </span>
                                    <span style={{ color: '#2d3748' }}>{item.employeeEmail || '—'}</span>
                                </div>
                                <div>
                                    <span style={{ color: '#718096' }}>Employee Doc ID: </span>
                                    <code style={{ backgroundColor: '#edf2f7', padding: '2px 5px', borderRadius: '4px', fontSize: '12px', color: '#4a5568' }}>
                                        {item.employeeDocumentID || '—'}
                                    </code>
                                </div>
                            </div>
                        </div>

                        {/* Section 2: External Source Metadata */}
                        <div style={{
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            padding: '14px 16px',
                        }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '700', color: '#4a5568', textTransform: 'uppercase' }}>
                                Onboarding360 Source Metadata
                            </h4>
                            <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div>
                                    <span style={{ color: '#718096' }}>Candidate User ID: </span>
                                    <code style={{ backgroundColor: '#edf2f7', padding: '2px 5px', borderRadius: '4px', fontSize: '12px', color: '#4a5568' }}>
                                        {getCandidateUserID(item)}
                                    </code>
                                </div>
                                <div>
                                    <span style={{ color: '#718096' }}>Reference #: </span>
                                    <span style={{ color: '#2d3748' }}>{item.payload?.reference_number || '—'}</span>
                                </div>
                                <div>
                                    <span style={{ color: '#718096' }}>Source Status: </span>
                                    <span style={{ color: '#2d3748' }}>{getCandidateSourceStatus(item)}</span>
                                    {item.normalizedStatus && (
                                        <span style={{ color: '#718096', marginLeft: '6px' }}>
                                            (Normalized: <strong>{item.normalizedStatus}</strong>)
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <span style={{ color: '#718096' }}>Agencies: </span>
                                    <span style={{ color: '#2d3748' }}>
                                        {getCandidateAgencies(item)}
                                    </span>
                                </div>
                                <div>
                                    <span style={{ color: '#718096' }}>Last Staged: </span>
                                    <span style={{ color: '#2d3748' }}>{formatDate(item.updatedAt || item.stagedAt)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: CAMHS Deep Review Panels or Quick Table (for non-CAMHS servers) */}
                    {serverID === '360camhs.co.uk' ? (
                        <div style={{ marginBottom: '16px' }}>
                            {!isRejected ? (
                                <>
                                    <FullProfileReview
                                        key={`profile:${serverID}:${item.id}:${employeeDocID}`}
                                        serverID={serverID}
                                        purgatoryID={item.id}
                                        employeeID={employeeDocID}
                                        item={item}
                                        onReject={isPending ? () => setShowRejectModal(true) : null}
                                        disabled={isBusy}
                                    />
                                    <LicenceReview
                                        key={`licence:${serverID}:${item.id}:${employeeDocID}`}
                                        serverID={serverID}
                                        purgatoryID={item.id}
                                        employeeID={employeeDocID}
                                        disabled={isBusy}
                                    />
                                    <TrainingReview
                                        key={`training:${serverID}:${item.id}:${employeeDocID}`}
                                        serverID={serverID}
                                        purgatoryID={item.id}
                                        employeeID={employeeDocID}
                                        disabled={isBusy}
                                    />
                                    <CompletionReview
                                        key={`completion:${serverID}:${item.id}`}
                                        serverID={serverID}
                                        purgatoryID={item.id}
                                        item={item}
                                        disabled={isBusy}
                                    />
                                </>
                            ) : (
                                <div style={{
                                    backgroundColor: '#fff5f5',
                                    borderRadius: '8px',
                                    border: '1px solid #fed7d7',
                                    padding: '16px',
                                    color: '#c53030',
                                    fontSize: '13px',
                                }}>
                                    <strong>Candidate Update Rejected:</strong> {item.rejectionReason || 'No reason provided.'}
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Values Comparison Table (Current vs Proposed) */}
                            <div style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                padding: '16px',
                                marginBottom: '16px',
                            }}>
                                <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '700', color: '#4a5568', textTransform: 'uppercase' }}>
                                    Account Changes Diff
                                </h4>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                                                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#4a5568', fontWeight: '600' }}>Field</th>
                                                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#4a5568', fontWeight: '600' }}>Current Live Value</th>
                                                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#4a5568', fontWeight: '600' }}>Proposed Value</th>
                                                <th style={{ textAlign: 'center', padding: '8px 12px', color: '#4a5568', fontWeight: '600' }}>Changed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Status field */}
                                            <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                                                <td style={{ padding: '8px 12px', fontWeight: '600', color: '#2d3748' }}>Account Active Status</td>
                                                <td style={{ padding: '8px 12px', color: '#718096' }}>
                                                    {String(item.currentValues?.status ?? '—')}
                                                </td>
                                                <td style={{ padding: '8px 12px', color: '#2d3748', fontWeight: '600' }}>
                                                    {String(item.proposedValues?.status ?? '—')}
                                                </td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                    {item.changedFieldNames?.includes('status') ? (
                                                        <span style={{ color: '#c53030', fontWeight: '700' }}>● YES</span>
                                                    ) : (
                                                        <span style={{ color: '#a0aec0' }}>—</span>
                                                    )}
                                                </td>
                                            </tr>

                                            {/* Employment Status field */}
                                            <tr style={{ borderBottom: '1px solid #edf2f7' }}>
                                                <td style={{ padding: '8px 12px', fontWeight: '600', color: '#2d3748' }}>Employment Status</td>
                                                <td style={{ padding: '8px 12px', color: '#718096' }}>
                                                    {item.currentValues?.employmentStatus || '—'}
                                                </td>
                                                <td style={{ padding: '8px 12px', color: '#2d3748', fontWeight: '600' }}>
                                                    {item.proposedValues?.employmentStatus || '—'}
                                                </td>
                                                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                    {item.changedFieldNames?.includes('employmentStatus') ? (
                                                        <span style={{ color: '#c53030', fontWeight: '700' }}>● YES</span>
                                                    ) : (
                                                        <span style={{ color: '#a0aec0' }}>—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Action Buttons for Pending Items */}
                            {isPending && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '12px',
                                    paddingTop: '12px',
                                    borderTop: '1px solid #edf2f7',
                                }}>
                                    <button
                                        type="button"
                                        onClick={() => setShowRejectModal(true)}
                                        disabled={isBusy}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            backgroundColor: '#ffffff',
                                            color: '#c53030',
                                            border: '1px solid #feb2b2',
                                            padding: '8px 18px',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            cursor: isBusy ? 'not-allowed' : 'pointer',
                                            opacity: isBusy ? 0.6 : 1,
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        <FaUserTimes />
                                        <span>Reject Update</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => onApprove(item)}
                                        disabled={isBusy}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            backgroundColor: '#276749',
                                            color: '#ffffff',
                                            border: 'none',
                                            padding: '8px 20px',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            cursor: isBusy ? 'not-allowed' : 'pointer',
                                            opacity: isBusy ? 0.6 : 1,
                                            boxShadow: '0 1px 3px rgba(39, 103, 73, 0.3)',
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        <FaUserCheck />
                                        <span>{isBusy ? 'Applying…' : 'Approve & Sync'}</span>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Rejection Modal Dialog */}
            {showRejectModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '500px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '700', color: '#c53030' }}>
                            Reject Candidate Update
                        </h3>
                        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#718096' }}>
                            Please state why this candidate update for <strong>{candidateName}</strong> is being rejected. This reason will be recorded in the audit trail.
                        </p>

                        <textarea
                            value={rejectReason}
                            onChange={(e) => {
                                setRejectReason(e.target.value);
                                setRejectError('');
                            }}
                            placeholder="Enter rejection reason (e.g. Incomplete documentation, Duplicate application, Identity mismatch)…"
                            rows={4}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                fontSize: '13px',
                                borderRadius: '6px',
                                border: `1px solid ${rejectError ? '#feb2b2' : '#cbd5e0'}`,
                                outline: 'none',
                                boxSizing: 'border-box',
                                resize: 'vertical',
                                color: '#2d3748',
                            }}
                        />

                        {rejectError && (
                            <p style={{ color: '#c53030', fontSize: '12px', margin: '6px 0 0 0', fontWeight: '600' }}>
                                {rejectError}
                            </p>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setRejectReason('');
                                    setRejectError('');
                                }}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmReject}
                                style={{
                                    backgroundColor: '#c53030',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                }}
                            >
                                Confirm Rejection
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurgatoryCard;
